import React from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { ByZyLogo } from './ByZyLogo';
import { ParticleOverlay } from './ParticleOverlay';
import { TOKENS } from '../lib/designTokens';
import { taglineSegments } from '../data/mock';
import { getText, getStyle } from '../config/overrides';
import { ChevronDown } from 'lucide-react';

const useReducedMotion = () => {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
};

export const IntroContent = ({ onEnter, entered, onOpenQuote }) => {
  const reducedMotion = useReducedMotion();

  /* ── Typewriter: first-load sequential reveal, then weight alternation ── */
  const [typewriterDone, setTypewriterDone] = React.useState(() => {
    try { return sessionStorage.getItem('byzy_intro_seen') === '1'; } catch { return true; }
  });

  React.useEffect(() => {
    if (typewriterDone || reducedMotion) return;
    const timer = setTimeout(() => {
      setTypewriterDone(true);
      try { sessionStorage.setItem('byzy_intro_seen', '1'); } catch {}
    }, 2600);
    return () => clearTimeout(timer);
  }, [typewriterDone, reducedMotion]);

  const canAnimate = !reducedMotion && !entered && typewriterDone;

  /* ── Single shared progress 0→1 drives all baseline animation ── */
  const progress = useMotionValue(0);

  React.useEffect(() => {
    if (!canAnimate) { progress.set(0); return; }
    const controls = animate(progress, 1, {
      duration: 6,
      ease: 'linear',
      repeat: Infinity,
    });
    return () => controls.stop();
  }, [canAnimate, progress]);

  // Trapezoidal wave with ~2s hold at each end
  // Timing: 1s ramp up, 2s hold, 1s ramp down, 2s hold (6s total)
  const eased = useTransform(progress, v => {
    const ramp = 1 / 6; // ~1s of 6s cycle
    if (v < ramp) {
      const t = v / ramp;
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
    if (v < 0.5) return 1;
    if (v < 0.5 + ramp) {
      const t = (v - 0.5) / ramp;
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      return 1 - e;
    }
    return 0;
  });

  // Derived motion values — zero re-render, pure style binding
  const nounWght = useTransform(eased, [0, 1], [600, 200]);
  const verbWght = useTransform(eased, [0, 1], [200, 600]);
  const nounScale = useTransform(eased, [0, 1], [1, 0.97]);
  const verbScale = useTransform(eased, [0, 1], [0.97, 1]);
  const nounColor = useTransform(eased, [0, 1], ['#ffffff', '#f0f0f2']);
  const verbColor = useTransform(eased, [0, 1], ['#f0f0f2', '#ffffff']);
  const nounFVS = useTransform(nounWght, w => `"wght" ${w}`);
  const verbFVS = useTransform(verbWght, w => `"wght" ${w}`);

  return (
    <div
      className="intro-inner"
      onClick={onEnter}
      role="button"
      tabIndex={0}
      aria-label="Entrer dans l'univers BY ZY"
      data-testid="intro-trigger"
    >
      {!reducedMotion && (
        <motion.div
          className="intro-halo"
          animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0.65, 0.4] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          aria-hidden="true"
        />
      )}

      <motion.div
        className="intro-logo-wrap"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={
          entered
            ? { scale: 1.08, opacity: 0, transition: { duration: 0.45, ease: TOKENS.animation.ease.premium } }
            : { scale: 1, opacity: 1 }
        }
        transition={{ duration: 0.8, ease: TOKENS.animation.ease.premium }}
      >
        <ByZyLogo className="intro-logo" style={{ color: TOKENS.colors.text.primary }} showTracer={canAnimate} />
      </motion.div>

      {entered && !reducedMotion && <ParticleOverlay />}

      <motion.h1
        className={`intro-tagline${canAnimate ? ' intro-tagline--anim' : ''}`}
        data-edit-id="intro.tagline"
        style={getStyle('intro.tagline')}
        initial={{ opacity: 0, y: 14 }}
        animate={entered ? { opacity: 0, y: -8, transition: { duration: 0.25 } } : { opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: TOKENS.animation.ease.premium, delay: 0.3 }}
      >
        {taglineSegments.map((seg, i) => {
          const overrideStyle = getStyle(`intro.tagline.${i}`);
          return (
            <motion.span
              key={i}
              className={seg.bold ? 'intro-tagline--bold' : 'intro-tagline--light'}
              data-edit-id={`intro.tagline.${i}`}
              {...(!typewriterDone && !reducedMotion ? {
                initial: { opacity: 0, y: 8 },
                animate: { opacity: 1, y: 0 },
                transition: { delay: 0.6 + i * 0.25, duration: 0.4, ease: [0.4, 0, 0.2, 1] },
              } : {})}
              style={overrideStyle || (canAnimate ? {
                fontVariationSettings: seg.bold ? nounFVS : verbFVS,
                scale: seg.bold ? nounScale : verbScale,
                color: seg.bold ? nounColor : verbColor,
              } : undefined)}
            >
              {getText(`intro.tagline.${i}`, seg.text).trimEnd()}
            </motion.span>
          );
        })}
      </motion.h1>

      <motion.button
        className="intro-cta-pill"
        initial={{ opacity: 0, y: 8 }}
        animate={entered ? { opacity: 0, y: -4 } : { opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.1, ease: TOKENS.animation.ease.premium }}
        onClick={(e) => { e.stopPropagation(); onOpenQuote?.(); }}
        data-testid="intro-cta"
      >
        Demander un devis
      </motion.button>

      <motion.div
        className="intro-hint-wrap"
        initial={{ opacity: 0 }}
        animate={entered ? { opacity: 0 } : { opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        {!reducedMotion ? (
          <motion.div
            className="intro-scroll-icon"
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            data-testid="intro-arrow"
          >
            <ChevronDown size={22} strokeWidth={1.5} />
          </motion.div>
        ) : (
          <div className="intro-scroll-icon" data-testid="intro-arrow">
            <ChevronDown size={22} strokeWidth={1.5} />
          </div>
        )}
      </motion.div>
    </div>
  );
};
